import { useCallback, useState } from 'react';
import { ArrowClockwise, ArrowLeft } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import { AsyncPage, LoadingButton, ToolAboutModal } from '@/components';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { ToolModel, ToolPreviewModel } from '@/lib/models';
import { toolsService } from '@/lib/services';

const useStyles = createUseThemedStyles((theme) => ({
	page: {
		display: 'flex',
		minHeight: 'calc(100vh - 60px)',
		flexDirection: 'column',
		backgroundColor: theme.colors.gray100,
	},
	toolbar: {
		display: 'flex',
		gap: 16,
		padding: '18px 24px',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: theme.colors.white,
		borderBottom: `1px solid ${theme.colors.gray300}`,
		'@media (max-width: 575px)': {
			alignItems: 'flex-start',
			flexDirection: 'column',
		},
	},
	title: {
		margin: 0,
		color: theme.colors.primary,
		...theme.fonts.m,
	},
	titleGroup: {
		display: 'flex',
		alignItems: 'baseline',
		flexWrap: 'wrap',
		gap: 12,
	},
	previewStatus: {
		paddingLeft: 12,
		borderLeft: `3px solid ${theme.colors.warning}`,
		color: theme.colors.gray700,
		...theme.fonts.s,
		fontWeight: 600,
	},
	toolbarActions: {
		display: 'flex',
		gap: 8,
		alignItems: 'center',
	},
	workspace: {
		flex: 1,
		margin: 24,
		minHeight: 560,
		display: 'flex',
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
	},
	iframe: {
		width: '100%',
		border: 0,
	},
}));

export const ToolLaunch = () => {
	const classes = useStyles();
	const handleError = useHandleError();
	const { toolId, toolVersionId } = useParams<{ toolId: string; toolVersionId?: string }>();
	const isPreview = Boolean(toolVersionId);
	const [tool, setTool] = useState<ToolModel | ToolPreviewModel>();
	const [launchUrl, setLaunchUrl] = useState('');
	const [showAbout, setShowAbout] = useState(false);
	const [isReloading, setIsReloading] = useState(false);

	const loadLaunchUrl = useCallback(async () => {
		if (!toolId) {
			throw new Error('The accelerator identifier is missing.');
		}
		const response = toolVersionId
			? await toolsService.launchToolPreview(toolId, toolVersionId).fetch()
			: await toolsService.launchTool(toolId).fetch();
		setLaunchUrl(response.launchUrl);
	}, [toolId, toolVersionId]);

	const fetchTool = useCallback(async () => {
		if (!toolId) {
			throw new Error('The accelerator identifier is missing.');
		}
		const toolRequest = toolVersionId
			? toolsService.getToolPreview(toolId, toolVersionId)
			: toolsService.getTool(toolId);
		const [toolResponse] = await Promise.all([toolRequest.fetch(), loadLaunchUrl()]);
		setTool(toolResponse.tool);
	}, [loadLaunchUrl, toolId, toolVersionId]);

	const reloadTool = useCallback(async () => {
		setIsReloading(true);
		try {
			await loadLaunchUrl();
		} catch (error) {
			handleError(error);
		} finally {
			setIsReloading(false);
		}
	}, [handleError, loadLaunchUrl]);

	return (
		<AsyncPage fetchData={fetchTool} errorDisplayClassName="m-0">
			<Helmet>
				<title>Hackett Connect - {tool?.title ?? 'Accelerator'}</title>
			</Helmet>
			<div className={classes.page}>
				<div className={classes.toolbar}>
					<div className={classes.titleGroup}>
						<Link
							className="p-2"
							to={isPreview ? `/tools/${toolId}/versions/${toolVersionId}/preview` : `/tools/${toolId}`}
							aria-label="Back to accelerator details"
							title="Back to accelerator details"
						>
							<ArrowLeft aria-hidden="true" size={18} />
						</Link>
						<h1 className={classes.title}>{tool?.title}</h1>
						{isPreview && <span className={classes.previewStatus}>Unpublished preview</span>}
					</div>
					<div className={classes.toolbarActions}>
						<LoadingButton
							isLoading={isReloading}
							variant="link"
							className="p-2"
							aria-label="Reload accelerator"
							title="Reload accelerator"
							onClick={reloadTool}
						>
							<ArrowClockwise size={18} />
						</LoadingButton>
						<Button variant="outline-primary" onClick={() => setShowAbout(true)}>
							About this accelerator
						</Button>
					</div>
				</div>
				<div className={classes.workspace}>
					{launchUrl && (
						<iframe
							className={classes.iframe}
							src={launchUrl}
							title={tool?.title ?? 'Hackett Connect accelerator'}
							sandbox="allow-scripts allow-modals allow-downloads"
							referrerPolicy="no-referrer"
						/>
					)}
				</div>
			</div>
			<ToolAboutModal tool={tool} show={showAbout} onHide={() => setShowAbout(false)} onToolChange={setTool} />
		</AsyncPage>
	);
};
